import { useCallback, useRef, useState } from "react"
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Menu, MenuItem, Switch, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { MoreHoriz, PlayArrow } from "@mui/icons-material"
import { useIsScheduledWorkflowMutating, useIsScheduledWorkflowRunStarted, useScheduledWorkflowLifecycle, useStartScheduledWorkflowRun } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowListItem } from "resources/scheduled-workflows/types"
import { activateErrorMessage, pauseErrorMessage, RUN_ARCHIVED_COPY, RUN_STARTED_UNRECORDED_COPY, startRunErrorMessage } from "./activate-error"
import { latestReportOf } from "./latest-report"

const ROW_ACTIONS_SX = { display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 0.5 } satisfies SxProps<Theme>
const RECONNECT_SX = { alignSelf: "flex-start", p: 0, minWidth: 0, textTransform: "none" } satisfies SxProps<Theme>
const ARCHIVE_ERROR_MESSAGE = () => "Couldn't archive this workflow. Please try again."
const CHECKED_SWITCH_BASE = "& .MuiSwitch-switchBase.Mui-checked, & .MuiSwitch-switchBase.Mui-checked.Mui-disabled"
const SWITCH_SX = {
  [CHECKED_SWITCH_BASE]: { color: "var(--app-primary)" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track, & .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track": {
    backgroundColor: "var(--app-primary)",
    opacity: 0.5,
  },
} satisfies SxProps<Theme>

function useActivateWithNotification(workflow: ScheduledWorkflowListItem) {
  const activate = useScheduledWorkflowLifecycle("activate", activateErrorMessage)
  const isPending = useIsScheduledWorkflowMutating("activate", workflow.scheduledWorkflowId)
  const run = useCallback(() => activate.mutate(workflow.scheduledWorkflowId), [activate, workflow.scheduledWorkflowId])
  return { run, isPending }
}

export function ReconnectButton({ workflow }: { workflow: ScheduledWorkflowListItem }) {
  const activate = useActivateWithNotification(workflow)
  if (workflow.archivedAt != null || workflow.blockedReason !== "credential_invalid") return null
  return (
    <Button size="small" variant="text" sx={RECONNECT_SX} disabled={activate.isPending} onClick={activate.run}>
      Reconnect
    </Button>
  )
}

export function RecoverButton({ workflow }: { workflow: ScheduledWorkflowListItem }) {
  const activate = useActivateWithNotification(workflow)
  if (workflow.archivedAt != null || workflow.blockedReason !== "repeated_failures") return null
  return (
    <Button size="small" variant="text" sx={RECONNECT_SX} disabled={activate.isPending} onClick={activate.run}>
      Recover
    </Button>
  )
}

export function RowActions({ workflow }: { workflow: ScheduledWorkflowListItem }) {
  const activate = useActivateWithNotification(workflow)
  const pause = useScheduledWorkflowLifecycle("pause", pauseErrorMessage)
  const archive = useScheduledWorkflowLifecycle("archive", ARCHIVE_ERROR_MESSAGE)
  const startRun = useStartScheduledWorkflowRun(startRunErrorMessage)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isConfirmingArchive, setIsConfirmingArchive] = useState(false)
  const isStartingRunRef = useRef(false)
  const isArchivingRef = useRef(false)
  const id = workflow.scheduledWorkflowId
  const menuId = `row-actions-menu-${id}`
  const isPausing = useIsScheduledWorkflowMutating("pause", id)
  const isStartingRun = useIsScheduledWorkflowMutating("runs", id)
  const isRunStarted = useIsScheduledWorkflowRunStarted(id)
  const isArchiving = useIsScheduledWorkflowMutating("archive", id)
  const isOn = workflow.status === "active" && workflow.blockedReason == null && workflow.archivedAt == null
  const isManualOnly = workflow.schedule == null
  const isArchived = workflow.archivedAt != null
  const isBlocked = workflow.blockedReason != null
  const isRunning = latestReportOf(workflow.lastRun, workflow.latestDeliverable).state === "running"

  const toggle = () => {
    if (isOn) pause.mutate(id)
    else activate.run()
  }
  const runNow = () => {
    if (isStartingRunRef.current) return
    isStartingRunRef.current = true
    startRun.mutate(id, { onSettled: () => { isStartingRunRef.current = false } })
  }
  const confirmArchive = () => {
    setMenuAnchor(null)
    setIsConfirmingArchive(true)
  }
  const archiveWorkflow = () => {
    setIsConfirmingArchive(false)
    if (isArchivingRef.current) return
    isArchivingRef.current = true
    archive.mutate(id, { onSettled: () => { isArchivingRef.current = false } })
  }

  const scheduleSwitch = (
    <Switch
      size="small"
      checked={isOn}
      sx={SWITCH_SX}
      disabled={isArchived || activate.isPending || isPausing}
      onChange={toggle}
      slotProps={{ input: { "aria-label": `${workflow.name} schedule` } }}
    />
  )

  return (
    <Box sx={ROW_ACTIONS_SX}>
      {!isManualOnly && !isBlocked && scheduleSwitch}
      <Tooltip title={isArchived ? RUN_ARCHIVED_COPY : isRunStarted ? RUN_STARTED_UNRECORDED_COPY : isRunning ? "Running now…" : "Run now"}>
        <span>
          <IconButton size="small" aria-label={`Run ${workflow.name} now`} disabled={isArchived || isStartingRun || isRunStarted || isRunning} onClick={runNow}>
            <PlayArrow fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {!isArchived && (
        <>
          <IconButton
            size="small"
            aria-label={`More actions for ${workflow.name}`}
            aria-haspopup="menu"
            aria-expanded={menuAnchor != null}
            aria-controls={menuAnchor != null ? menuId : undefined}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
          <Menu id={menuId} anchorEl={menuAnchor} open={menuAnchor != null} onClose={() => setMenuAnchor(null)}>
            <MenuItem disabled={isArchiving} onClick={confirmArchive}>Archive</MenuItem>
          </Menu>
        </>
      )}
      <Dialog open={isConfirmingArchive} onClose={() => setIsConfirmingArchive(false)} maxWidth="xs">
        <DialogTitle>{`Archive "${workflow.name}"?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>It will stop running. This can&apos;t be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmingArchive(false)}>Cancel</Button>
          <Button onClick={archiveWorkflow} color="error" variant="contained">Archive</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
